import React, { useState, useEffect, forwardRef } from 'react';
import { userRoleService, userGroupService } from '../services';
import {
  Checkbox,
  formatSelectOptions,
  Input,
  SelectButtons,
  TextareaAutosize,
  SideDialogSearchResult,
} from '../components';
import { authMethod, getBatchEditValue, formatData } from '../helpers';
import { useGlobalState, useDebounce, useMessage } from '../hooks';

const _AddUserGroupForm = (
  { mode, data, submitForm, loading, setLoading, formErrors = {} },
  ref
) => {
  const [settings] = useGlobalState('settings');

  const initialFormState = {
    user_group_type: settings.authMethods[0],
    user_group_id: null,
    user_group_source_id: null,
    user_group_name: '',
    user_group_email: undefined,
    user_group_description: '',
    user_group_default_role_id: '',
    user_provisioning_is_enabled: false,
  };

  const initialBatchEditItems = {
    batch_user_group_default_role_id: false,
    batch_user_provisioning_is_enabled: false,
  };

  const [loggedInUser, setLoggedInUser] = useGlobalState('loggedInUser');
  const [displayNames] = useGlobalState('displayNames');
  const [formData, setFormData] = useState(initialFormState);
  const [batchEdit, setBatchEdit] = useState(false);
  const [batchEditItems, setBatchEditItems] = useState(initialBatchEditItems);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [accessToken, setAccessToken] = useState();

  const { showMessage, hideMessage } = useMessage();

  useEffect(() => {
    // Get User Roles
    fetchUserRoles();
  }, []);

  useEffect(() => {
    setBatchEdit(false);
    if (data.length === 1) {
      setFormData({
        user_group_type: data[0].user_group_type,
        user_group_id: data[0].user_group_id,
        user_group_name: data[0].user_group_name || '',
        user_group_email: data[0].user_group_email || undefined,
        user_group_description: data[0].user_group_description || '',
        user_group_default_role_id: data[0].user_group_default_role_id,
        user_provisioning_is_enabled: data[0].user_provisioning_is_enabled || false,
      });
    } else if (data.length > 1 && mode !== 'add') {
      setBatchEdit(true);

      setFormData({
        user_group_default_role_id: getBatchEditValue(data, 'user_group_default_role_id'),
        user_provisioning_is_enabled: getBatchEditValue(data, 'user_provisioning_is_enabled'),
      });
    } else if (mode === 'add') {
      setFormData({
        ...formData,
        // userRoles should have been sorted in asc order by permission_level
        user_group_default_role_id: userRoles[0]?.user_role_id || '',
      });
    } else {
      resetForm();
    }
  }, [mode, data, userRoles]);

  const fetchSearchResults = useDebounce(async search => {
    try {
      //setLoadingSearch(true)
      const userGroups = await userGroupService.getFromAD(search, accessToken);

      setAccessToken(userGroups?.access_token);

      if (userGroups?.user_groups.length > 0) {
        setItems(prev => {
          return userGroups.user_groups.map(x => {
            if (selectedItems.find(y => y.user_group_source_id === x.user_group_source_id)) {
              return { ...x, selected: true };
            }
            return x;
          });
        });
        setLoadingSearch(false);
      } else if (userGroups === undefined) {
        // Do nothing
        // userGroups is undefined if request was aborted
      } else {
        setItems(prev => {
          return [];
        });
        setLoadingSearch(false);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setLoadingSearch(false);
      }
      showMessage(err, 'error');
    }
  }, 200).debounce;

  const fetchUserRoles = async () => {
    userRoleService
      .getAll()
      .then(res => {
        if (res?.user_roles) {
          let tmp = res.user_roles;

          // Sort by permission level.
          // This is to use the lowest level as default user role.
          tmp.sort(function (a, b) {
            var keyA = new Date(a.permission_level),
              keyB = new Date(b.permission_level);

            if (keyA < keyB) return -1;
            if (keyA > keyB) return 1;
            return 0;
          });

          setUserRoles(tmp);
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const handleInputChange = (event, formPart) => {
    let { name, value } = '';

    // Handle standard form inputs
    if (event.target !== undefined) {
      name = event.target.name;
      value = event.target.value;

      // If input element is a checkbox, we cannot use "value"
      if (event.target.type === 'checkbox') {
        value = event.target.checked;
      }

      // Handle custom form inputs
    } else {
      name = event.name;
      value = event.value;
    }

    switch (name) {
      case 'search_user_group':
        setSearchTerm(value);

        if (value.length > 1) {
          setLoadingSearch(true);
          fetchSearchResults(value);
        } else {
          setItems(prev => {
            return [];
          });
        }
        break;
      default:
        break;
    }

    switch (formPart) {
      case 'batch':
        setBatchEditItems({ ...batchEditItems, [name]: value });
        break;
      default:
        setFormData({ ...formData, [name]: value });
    }

    // Hide errors on input change
    if (formErrors && formErrors[name]) {
      delete formErrors[name];
      hideMessage();
    }
  };

  const onSubmit = event => {
    event.preventDefault();

    setLoading(true);

    // Form validation
    //if (!formData.user_fullname || !formData.user_username) return

    let dataToSubmit;

    if (batchEdit) {
      let key = '';
      let tmp = {};
      const objects = Object.entries(batchEditItems);

      for (let i = 0; i < objects.length; i++) {
        if (objects[i][1]) {
          key = objects[i][0].replace('batch_', '');
          tmp = { ...tmp, [key]: formData[key] };
        }
      }

      dataToSubmit = data.map(item => {
        return {
          user_group_type: item.user_group_type,
          user_group_id: item.user_group_id,
          user_group_source_id: item.user_group_source_id,
          user_group_name: item.user_group_name,
          user_group_email: item.user_group_email,
          user_group_description: item.user_group_description,
          user_group_default_role_id: item.user_group_default_role_id,
          user_provisioning_is_enabled: item.user_provisioning_is_enabled,
          ...tmp,
        };
      });
    } else if (formData.user_group_type === 'oidc' && mode === 'add') {
      dataToSubmit = selectedItems.map(item => {
        return {
          user_group_type: item.user_group_type,
          user_group_id: item.user_group_id,
          user_group_source_id: item.user_group_source_id,
          user_group_name: item.user_group_name,
          user_group_email: item.user_group_email,
          user_group_description: item.user_group_description,
          user_group_default_role_id: formData.user_group_default_role_id,
          user_provisioning_is_enabled: formData.user_provisioning_is_enabled,
        };
      });
    } else {
      dataToSubmit = formData;

      // Delete supporting properties
      delete dataToSubmit.search_user_group;
    }

    // Submit form
    submitForm(dataToSubmit)
      .then(res => resetForm())
      .catch(err => {
        // Errors handled in submitForm function
      })
      .finally(res => setLoading(false));
  };

  const selectItem = item => {
    if (!selectedItems.find(x => x.user_group_source_id === item.user_group_source_id)) {
      setSelectedItems(prev => {
        return [...prev, item];
      });

      setItems(prev => {
        return prev.map(x => {
          if (x.user_group_source_id === item.user_group_source_id) {
            return { ...x, selected: true };
          }
          return x;
        });
      });
    }
  };

  const removeItem = item => {
    setSelectedItems(prev =>
      prev.filter(x => x.user_group_source_id !== item.user_group_source_id)
    );

    setItems(prev => {
      return prev.map(x => {
        if (x.user_group_source_id === item.user_group_source_id) {
          return { ...x, selected: false };
        }
        return x;
      });
    });
  };

  const resetForm = () => {
    setBatchEdit(false);
    setItems([]);
    setSelectedItems([]);
    setBatchEditItems(initialBatchEditItems);
    setSearchTerm('');
    setFormData(prevFormData => {
      return {
        ...initialFormState,
        user_group_type: prevFormData.user_group_type,
        user_group_default_role_id: userRoles[0]?.user_role_id || '',
      };
    });
  };

  const userTypes = settings.authMethods.map(x => {
    const method = Object.entries(authMethod).find(([key, value]) => x === key);
    if (method) {
      const [key, value] = method;
      return { value: key, label: value };
    }
  });

  return (
    <div className="form">
      <form onSubmit={onSubmit} ref={ref}>
        {!batchEdit && userTypes.length > 1 && (
          <div className="form-block vertical">
            <SelectButtons
              name="user_group_type"
              label="User Group Type"
              value={formData.user_group_type}
              options={formatSelectOptions({
                options: userTypes,
                optionValue: 'value',
                optionLabel: 'label',
              })}
              onChange={handleInputChange}
              disabled={loading || mode !== 'add'}
            />
          </div>
        )}

        <div className="form-block vertical">
          {batchEdit ? (
            <label>
              <Checkbox
                checked={batchEditItems.batch_user_provisioning_is_enabled}
                name="batch_user_provisioning_is_enabled"
                label="Edit User Provisioning"
                onChange={e => handleInputChange(e, 'batch')}
                disabled={loading}
              />
            </label>
          ) : null}

          <Checkbox
            name="user_provisioning_is_enabled"
            checked={formData.user_provisioning_is_enabled}
            onChange={handleInputChange}
            label="Enable User Provisioning"
            title={displayNames.user_provisioning_is_enabled.display_name_description}
            disabled={loading || (batchEdit && !batchEditItems.batch_user_provisioning_is_enabled)}
          />
        </div>

        {formData.user_provisioning_is_enabled && (
          <div className="form-block vertical">
            {batchEdit && (
              <label>
                <Checkbox
                  checked={batchEditItems.batch_user_group_default_role_id}
                  name="batch_user_group_default_role_id"
                  label="Edit Default User Role"
                  onChange={e => handleInputChange(e, 'batch')}
                  disabled={loading}
                />
              </label>
            )}
            <SelectButtons
              name="user_group_default_role_id"
              label={!batchEdit && 'Default User Role'}
              value={formData.user_group_default_role_id}
              options={formatSelectOptions({
                options: userRoles,
                optionValue: 'user_role_id',
                optionLabel: 'user_role_name',
              })}
              onChange={handleInputChange}
              title={displayNames.user_group_default_role_name.display_name_description}
              disabled={loading || (batchEdit && !batchEditItems.batch_user_group_default_role_id)}
            />
          </div>
        )}

        {formData.user_group_type === 'oidc' && mode === 'add' && (
          <React.Fragment>
            <h4>User Groups</h4>
            <div className="form-block vertical side-dialog-search-input">
              <Input
                type="text"
                name="search_user_group"
                placeholder="Search user groups to add..."
                value={searchTerm}
                onChange={handleInputChange}
                disabled={loading}
                isNullable={true}
              />
              {loadingSearch && (
                <span className="side-dialog-search-loading-indicator">Searching...</span>
              )}
              {!loadingSearch && searchTerm.length > 1 && (
                <span className="side-dialog-search-result-count">
                  {items.length} user groups found
                </span>
              )}
            </div>

            {items.length > 0 && (
              <div className="form-block vertical">
                {items.map((item, index) => {
                  return (
                    <SideDialogSearchResult
                      key={index}
                      item={item}
                      title={item.user_group_name}
                      description={item.user_group_description}
                      photo={formatData(
                        item.user_group_name,
                        'owner',
                        undefined,
                        undefined,
                        undefined,
                        { photo: item.user_group_photo }
                      )}
                      selected={item.selected}
                      onClick={() => !item?.selected && selectItem(item)}
                    />
                  );
                })}
              </div>
            )}
            {selectedItems.length > 0 && (
              <div className="form-block vertical">
                <label className="form-strong-text">
                  {selectedItems.length} User Groups to add
                </label>
                {selectedItems.map((item, index) => {
                  return (
                    <SideDialogSearchResult
                      key={index}
                      item={item}
                      title={item.user_group_name}
                      description={item.user_group_description}
                      photo={formatData(
                        item.user_group_name,
                        'owner',
                        undefined,
                        undefined,
                        undefined,
                        { photo: item.user_group_photo }
                      )}
                      selected={item.selected}
                      onClick={removeItem}
                    />
                  );
                })}
              </div>
            )}
          </React.Fragment>
        )}

        {!batchEdit && (formData.user_group_type !== 'oidc' || mode === 'edit') && (
          <div className="form-block vertical">
            <Input
              type="text"
              name="user_group_name"
              label="Name"
              value={formData.user_group_name}
              onChange={handleInputChange}
              autoComplete="off"
              disabled={loading || formData.user_group_type === 'oidc'}
              validation={
                formErrors.user_group_name
                  ? { valid: false, message: formErrors.user_group_name }
                  : undefined
              }
            />
          </div>
        )}

        {formData.user_group_type === 'local' && (
          <React.Fragment>
            {!batchEdit && (
              <div className="form-block vertical">
                <TextareaAutosize
                  label="Description"
                  name="user_group_description"
                  value={formData.user_group_description}
                  onChange={handleInputChange}
                  disabled={loading}
                  isOptional={true}
                />
              </div>
            )}
            {!batchEdit && (
              <div className="form-block vertical">
                <Input
                  type="email"
                  name="user_group_email"
                  label="Email"
                  value={formData.user_group_email}
                  onChange={handleInputChange}
                  disabled={loading}
                  validation={
                    formErrors.user_group_email
                      ? { valid: false, message: formErrors.user_group_email }
                      : undefined
                  }
                  isOptional={true}
                />
              </div>
            )}
          </React.Fragment>
        )}
      </form>
    </div>
  );
};

export const AddUserGroupForm = forwardRef(_AddUserGroupForm);
